import { ReactElement, ReactNode } from "react";
import { useTranslation } from "@shared/utils/internationalization";
import { DataProvider } from "@shared/constants/common";
import { SeatingStateProvider } from "@shared/modules/seating/contexts/useSeatingState";
import { WizardProvider } from "@shared/contexts/WizardContext/WizardContext";
import { useWizard } from "@shared/hooks/useWizard/useWizard";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import useRedirect from "@shared/hooks/useRedirect";
import { useRouter } from "next/router";

import Box from "@shared/components/ui-kit/layout/Box/Box.web";
import Flex from "@shared/components/ui-kit/layout/Flex/Flex.web";
import Text from "@shared/components/ui-kit/typography/Text/Text.web";
import Button from "@shared/components/ui-kit/form/Button/Button.web";
import ItemGroup from "@shared/components/application/data-display/item-group/ItemGroup.web";
import CheckoutExpiration from "@shared/modules/checkout/components/expiration/CheckoutExpiration/CheckoutExpiration.web";

import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";
import CheckoutNavBarHeader from "@app-contexts/public/features/checkout/components/CheckoutNavBarHeader/CheckoutNavBarHeader.web";
import Icon from "@shared/components/ui-kit/icons/Icon.web";
import CheckoutBaseLayout from "../../templates/CheckoutBaseLayout/CheckoutBaseLayout.web";
import CheckoutBaseLayoutWithCart from "../../templates/CheckoutBaseLayoutWithCart/CheckoutBaseLayoutWithCart.web";
import CheckoutSummary from "../CheckoutSummary/CheckoutSummary.web";
import ReviewItemGroup from "../steps/review/components/ReviewItemGroup/ReviewItemGroup.web";
import { CheckoutTrackingProvider } from "../../contexts/useCheckoutTrackingContext";

interface CheckoutReviewProps {
  isLoading: boolean;
  header?: ReactNode;
  onCheckout?: () => Promise<void> | void;
  isEmptyOverride?: boolean;
  hasError?: boolean;
  children?: ReactNode;
  customSummary?: ReactNode;
}

/**
 * This component is used to display a review page of a basket object before the checkout flow is
 * started.
 *
 * For the review page used in checkout flow, see Review.web.tsx.
 */
function CheckoutReview({
  isLoading,
  header,
  onCheckout,
  isEmptyOverride,
  hasError,
  children,
  customSummary,
}: CheckoutReviewProps): ReactElement {
  const { t } = useTranslation("checkout");
  const router = useRouter();
  const basket = usePublicBasket();
  const wizard = useWizard();
  const { redirect } = useRedirect();

  // Get the current URL for redirects
  const currentUrl = router.asPath;

  // Default checkout handler
  const handleCheckout = async () => {
    if (onCheckout) {
      await onCheckout();
    } else {
      redirect("/checkout", { asWindow: true });
    }
  };

  // Default header if none provided
  const defaultHeader = <CheckoutNavBarHeader />;

  // Standard empty state
  const emptyState = (
    <CheckoutExpiration
      // For restarts, we'll just refresh the current page.
      handleRestart={() => redirect(currentUrl, { asWindow: true })}
    />
  );

  // Standard right column with default CheckoutSummary
  const defaultRightColumn = (
    <WizardProvider wizard={wizard}>
      {!isLoading && (
        <CheckoutSummary
          allowApplyingDiscounts={false}
          callToActionButton={
            <Button.Solid
              data-testid="checkout-review-checkout-button"
              onClick={handleCheckout}
              height="element.4xl"
            >
              {t("Checkout")}
            </Button.Solid>
          }
        />
      )}
    </WizardProvider>
  );

  // Use custom summary if provided, otherwise use the default
  const rightColumn = customSummary || defaultRightColumn;

  // Use the provided override if available, otherwise use the basket state
  const isEmptyOrExpired =
    isEmptyOverride !== undefined
      ? isEmptyOverride
      : basket.isExpired || basket.totalItemQuantity === 0;

  return (
    <SeatingStateProvider
      isOnSeatingMapPage={false}
      basketType={DataProvider.Public}
    >
      <Box display="flex" flexGrow={1} data-testid="checkout-review">
        <CheckoutBaseLayout>
          <CheckoutBaseLayout.Header>
            {header || defaultHeader}
          </CheckoutBaseLayout.Header>
          <CheckoutBaseLayout.Content>
            {!hasError ? (
              <>
                {isEmptyOrExpired && !isLoading ? (
                  emptyState
                ) : (
                  <CheckoutBaseLayoutWithCart rightColumn={rightColumn}>
                    {children}
                  </CheckoutBaseLayoutWithCart>
                )}
              </>
            ) : (
              <CheckoutReview.Error />
            )}
          </CheckoutBaseLayout.Content>
        </CheckoutBaseLayout>
      </Box>
    </SeatingStateProvider>
  );
}

interface ContentProps {
  children: ReactNode;
}

function Content({ children }: ContentProps): ReactElement {
  const { t } = useTranslation("checkout");

  return (
    <Flex p={{ base: "lg", sm: "xl" }} gap="md" direction="column" bg="bg">
      <Box>
        <Text fontSize="xl" fontWeight="bold" data-testid="review-page-heading">
          {t("Review")}
        </Text>
      </Box>
      {children}
    </Flex>
  );
}

function LoadingSkeleton(): ReactElement {
  return (
    <Box data-testid="checkout-review-skeleton">
      <ItemGroup.Skeleton />
    </Box>
  );
}

interface ItemsProps {
  itemGroups: Array<TicketItemGroupType>;
  allowQuantityChange?: boolean;
}

function Items({
  itemGroups,
  allowQuantityChange = true,
}: ItemsProps): ReactElement {
  return (
    <Flex direction="column" gap="md" data-testid="checkout-review-items">
      <CheckoutTrackingProvider>
        {itemGroups.map((itemGroup) => (
          <ReviewItemGroup
            key={itemGroup.id}
            itemGroup={itemGroup}
            allowQuantityChange={allowQuantityChange}
          />
        ))}
      </CheckoutTrackingProvider>
    </Flex>
  );
}

function Error() {
  const { t } = useTranslation("checkout");
  return (
    <Flex
      w="100%"
      justifyContent="center"
      alignItems="center"
      data-testid="checkout-review-error"
    >
      <Flex direction="column" alignItems="center" textAlign="center">
        <Icon.Cart.Solid
          boxSize={{ base: "element.5xl", xs: "element.6xl" }}
          colorPalette="primary"
        />
        <Text mt="md" fontSize={{ base: "4xl", xs: "5xl" }} fontWeight={700}>
          {t("Oops! We couldn't find what you were looking for!")}
        </Text>
        <Text mt="xs" fontSize={{ base: "sm", xs: "md" }}>
          {t("Please double-check the URL or contact the event organizer.")}
        </Text>
      </Flex>
    </Flex>
  );
}

CheckoutReview.Content = Content;
CheckoutReview.LoadingSkeleton = LoadingSkeleton;
CheckoutReview.Items = Items;
CheckoutReview.Error = Error;

export default CheckoutReview;
